/*
 * certificate-maker/layout.js — the document model resolved into millimetres.
 *
 * ONE LAYOUT, TWO RENDERERS
 * ─────────────────────────
 * This file turns a document description into a flat list of primitives in
 * millimetres, y down from the top-left of the page. render.js draws that list
 * as SVG on screen; pdf.js draws the SAME list into a PDF. Neither of them
 * makes a positioning decision. That is the only way "на экране было нормально,
 * а напечаталось не так" becomes structurally impossible rather than a thing we
 * keep fixing.
 *
 * MEASUREMENT IS INJECTED, NOT ASSUMED
 * ────────────────────────────────────
 * `measure(text, style) → {w, asc, desc}` in millimetres is supplied by the
 * caller: canvas metrics on screen, the embedded face's own metrics in the PDF,
 * a synthetic ruler in the node tests. Both real implementations read the SAME
 * TTF, so they agree; and because the ASCENT is measured rather than guessed,
 * a display face with tall capitals sits on the same baseline in both. Guessing
 * it as "0.78 em" is exactly how /label-maker put its text 4 mm low on paper
 * while looking right on screen (2026-08-20).
 *
 * WHY TEXT NEVER OVERFLOWS
 * ────────────────────────
 * A certificate has one field whose length the author does not control — the
 * recipient's name — and the batch case makes that unavoidable: somewhere in a
 * class list there is a «Константинопольская Александра Владимировна». Every
 * competitor surveyed either overflows the frame or silently clips. Here every
 * block declares how it may adapt (shrink, then wrap, then admit defeat with a
 * warning the UI shows), so the output is either correct or reported — never
 * quietly wrong.
 */
(function (root, factory) {
    'use strict';
    var api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.CertLayout = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : null), function () {
    'use strict';

    /* ── page presets, millimetres ───────────────────────────────────────── */
    var PAGES = {
        a4l:  { w: 297, h: 210 },
        a4p:  { w: 210, h: 297 },
        a5l:  { w: 210, h: 148 },
        a5p:  { w: 148, h: 210 },
        letl: { w: 279.4, h: 215.9 },
        letp: { w: 215.9, h: 279.4 },
        a3l:  { w: 420, h: 297 },
        sq:   { w: 210, h: 210 }
    };

    function pageSize(doc) {
        var p = doc.page || {};
        if (p.preset === 'custom') return { w: +p.w || 297, h: +p.h || 210 };
        var s = PAGES[p.preset] || PAGES.a4l;
        return { w: s.w, h: s.h };
    }

    /* ── text fitting ────────────────────────────────────────────────────── */

    /* Greedy word wrap. A single word wider than the column is broken by
     * character — with a soft hyphen it would look better, but a name is the
     * usual culprit and hyphenating a surname is worse than breaking it. */
    function wrap(text, style, maxW, measure) {
        var words = String(text).split(/\s+/).filter(Boolean);
        if (!words.length) return [];
        var lines = [], cur = '';
        for (var i = 0; i < words.length; i++) {
            var probe = cur ? cur + ' ' + words[i] : words[i];
            if (measure(probe, style).w <= maxW || !cur) {
                if (measure(probe, style).w > maxW && !cur) {
                    // one word, still too wide: break it by character
                    var w = words[i], part = '';
                    for (var c = 0; c < w.length; c++) {
                        if (measure(part + w[c], style).w > maxW && part) { lines.push(part); part = ''; }
                        part += w[c];
                    }
                    cur = part;
                    continue;
                }
                cur = probe;
            } else {
                lines.push(cur);
                cur = words[i];
            }
        }
        if (cur) lines.push(cur);
        return lines;
    }

    /* Explicit newlines survive; each paragraph wraps on its own. An entirely
     * empty string has NO lines — not one empty line, which would reserve a
     * line's worth of height for nothing and push the composition down. */
    function wrapAll(text, style, maxW, measure) {
        var out = [];
        if (!String(text == null ? '' : text).trim()) return out;
        String(text).split(/\r?\n/).forEach(function (para) {
            if (!para.trim()) { out.push(''); return; }
            out = out.concat(wrap(para, style, maxW, measure));
        });
        return out;
    }

    /**
     * Shrink-then-wrap. Returns the largest size at which the text fits the
     * column, and reports honestly when it cannot.
     *
     * Order matters: shrinking a name by 8 % is invisible, wrapping it to two
     * lines is not — so size gives way first, and only down to `minRatio`
     * (below that the block stops matching the rest of the page and the wrap
     * is the lesser evil).
     *
     * `tightRatio` is the typesetter's extra pass: a long school name set in
     * spaced capitals wraps to a two-word orphan («… СРЕДНЯЯ ШКОЛА / № 12») long
     * before it is actually too big, and 14 % off the type size reads as nothing
     * while the orphan reads as a mistake. So a block may ask to try ONE line at
     * a shallow shrink before it accepts a second line at full size.
     */
    /* ⚠️ TRACKING SHRINKS WITH THE TYPE. Letter spacing is a proportion of the
     * em, not a fixed distance — a title set at half the size with the same
     * 5 mm of tracking is not smaller, it is just more spaced out. Holding it
     * fixed also makes the fitter unable to CONVERGE: «БЛАГОДАРСТВЕННОЕ ПИСЬМО»
     * is 22 gaps, so 110 mm of the column was tracking alone and no reduction
     * in size could ever fit it (measured 2026-08-23 — it reported overflow on
     * a page that had room). */
    function fitAt(text, style, maxW, measure, maxLines, minRatio) {
        var size = style.size, floor = style.size * minRatio;
        var ls0 = style.letterSpacing || 0;
        var st, lines;
        for (;;) {
            st = assign(style, { size: size, letterSpacing: ls0 * (size / style.size) });
            lines = wrapAll(text, st, maxW, measure);
            if (lines.length <= maxLines && widest(lines, st, measure) <= maxW + 0.05) {
                return { size: size, letterSpacing: st.letterSpacing, lines: lines,
                         shrunk: size < style.size - 1e-6, overflow: false };
            }
            if (size <= floor + 1e-6) break;
            size = Math.max(floor, size * 0.97);
        }
        st = assign(style, { size: floor, letterSpacing: ls0 * minRatio });
        lines = wrapAll(text, st, maxW, measure);
        var over = widest(lines, st, measure) > maxW + 0.05;
        return { size: floor, letterSpacing: ls0 * minRatio, lines: lines, shrunk: true,
                 overflow: over || lines.length > maxLines };
    }

    function fit(text, style, maxW, measure) {
        var maxLines = style.maxLines || 1;
        var minRatio = style.minRatio == null ? 0.62 : style.minRatio;

        if (style.tightRatio && maxLines > 1) {
            var one = fitAt(text, style, maxW, measure, 1, style.tightRatio);
            /* `tight` marks a shrink that was PREFERRED, not conceded: keeping a
               long organisation name on one line is the intended typography, so
               it must not be reported as a compromise. */
            if (!one.overflow) { one.tight = true; return one; }
        }

        var r = fitAt(text, style, maxW, measure, maxLines, minRatio);
        if (!r.overflow) return r;
        /* Last resort: one more line than asked for, at the floor size. Better a
         * three-line reason than a clipped one — but it is still reported, so
         * the batch review can flag the row. */
        var relaxed = fitAt(text, style, maxW, measure, maxLines + 1, minRatio);
        relaxed.overflow = relaxed.overflow || true;
        return relaxed;
    }

    function widest(lines, style, measure) {
        var m = 0;
        for (var i = 0; i < lines.length; i++) m = Math.max(m, measure(lines[i], style).w);
        return m;
    }

    function assign(a, b) {
        var o = {};
        for (var k in a) if (Object.prototype.hasOwnProperty.call(a, k)) o[k] = a[k];
        for (var j in b) if (Object.prototype.hasOwnProperty.call(b, j)) o[j] = b[j];
        return o;
    }

    /* ── colour resolution ───────────────────────────────────────────────── */
    /** A rounded rectangle as M/L/C only — the same restricted vocabulary the
     *  frames use, because pdf-lib's arc handling has rounding quirks on small
     *  radii and the preview would then stop matching the paper. */
    function roundedRect(x, y, w, h, r) {
        r = Math.max(0, Math.min(r, Math.min(w, h) / 2));
        var k = r * 0.5523;
        var n = function (v) { return Math.round(v * 100) / 100; };
        if (!r) {
            return 'M' + n(x) + ' ' + n(y) + 'L' + n(x + w) + ' ' + n(y)
                + 'L' + n(x + w) + ' ' + n(y + h) + 'L' + n(x) + ' ' + n(y + h) + 'Z';
        }
        return 'M' + n(x + r) + ' ' + n(y)
            + 'L' + n(x + w - r) + ' ' + n(y)
            + 'C' + n(x + w - r + k) + ' ' + n(y) + ' ' + n(x + w) + ' ' + n(y + r - k) + ' ' + n(x + w) + ' ' + n(y + r)
            + 'L' + n(x + w) + ' ' + n(y + h - r)
            + 'C' + n(x + w) + ' ' + n(y + h - r + k) + ' ' + n(x + w - r + k) + ' ' + n(y + h) + ' ' + n(x + w - r) + ' ' + n(y + h)
            + 'L' + n(x + r) + ' ' + n(y + h)
            + 'C' + n(x + r - k) + ' ' + n(y + h) + ' ' + n(x) + ' ' + n(y + h - r + k) + ' ' + n(x) + ' ' + n(y + h - r)
            + 'L' + n(x) + ' ' + n(y + r)
            + 'C' + n(x) + ' ' + n(y + r - k) + ' ' + n(x + r - k) + ' ' + n(y) + ' ' + n(x + r) + ' ' + n(y)
            + 'Z';
    }

    function colorOf(v, style) {
        if (!v || v === 'ink') return style.ink;
        if (v === 'accent') return style.accent;
        if (v === 'muted') return mix(style.ink, style.paper, 0.42);
        return v;
    }
    function mix(a, b, t) {
        var A = hex(a), B = hex(b);
        return '#' + [0, 1, 2].map(function (i) {
            var v = Math.round(A[i] + (B[i] - A[i]) * t);
            return ('0' + Math.max(0, Math.min(255, v)).toString(16)).slice(-2);
        }).join('');
    }
    function hex(h) {
        h = String(h || '#000').replace('#', '');
        if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
        return [parseInt(h.slice(0, 2), 16) || 0, parseInt(h.slice(2, 4), 16) || 0, parseInt(h.slice(4, 6), 16) || 0];
    }

    /* ── the build ───────────────────────────────────────────────────────── */

    /**
     * build(doc, measure, deps) → { prims, page, content, blocks, warnings }
     *
     * deps.frames — CertFrames (optional; omitted in pure-layout tests).
     */
    /** left/center/right → the SVG text-anchor vocabulary.
     *
     * ⚠️ Blocks and primitives MUST speak the same one. They did not, and the
     * whole editor hit-tested against a rectangle offset by half its own width
     * (see the header of this change). Anything that publishes an anchor goes
     * through here. */
    function svgAnchor(align) {
        return align === 'left' ? 'start' : align === 'right' ? 'end' : 'middle';
    }

    function build(doc, measure, deps) {
        deps = deps || {};
        _measure = measure;
        var page = pageSize(doc);
        var st = doc.style || {};
        var style = {
            ink: st.ink || '#1d2b45',
            accent: st.accent || '#b08b3f',
            paper: st.paper || '#fffdf7'
        };
        var prims = [], warnings = [];

        /* Paper. Drawn as a primitive rather than assumed by the renderer so a
         * PNG export and a PDF export cannot disagree about the background —
         * and so "white" and "none" stay distinguishable. */
        if (st.paper && st.paper !== 'none') {
            prims.push({ type: 'rect', x: 0, y: 0, w: page.w, h: page.h, fill: style.paper, color: null, sw: 0 });
        }

        /* ── the background layer ───────────────────────────────────────────
         * Under everything except the paper itself, and it may CLAIM ROOM. Two
         * sources, one slot: a parametric illustration built for this page
         * (backgrounds.js) or a picture the author brought.
         *
         * ⚠️ A custom picture is drawn to the FULL page and beyond, never
         * letterboxed: the sheet is trimmed, and a background that stops at the
         * trim line leaves a white hairline down one edge of every copy. The
         * export adds bleed around this same rectangle for the same reason.
         *
         * ⚠️ This is NOT the overprint backdrop. That one is a photograph of a
         * blank the author already bought, it exists to line the text up, and
         * `render.js` strips it before export on purpose. This layer prints. */
        var bgInset = null, bgKind = null;
        /* Where the illustration and then the scrim get spliced in: directly
           above the paper and below everything else. The art is BUILT later,
           once the text column is known — see «keep-out» below — but its place
           in the stack is decided here. */
        var bgIndex = -1;
        if (st.bgImage) {
            /* Cover-fit is computed HERE, once, from the aspect ratio the
             * uploader measured — not re-derived by each renderer from
             * `preserveAspectRatio`, a canvas source rect and a PDF clip. Those
             * are three different implementations of one decision, and they
             * disagree in the third decimal, which is exactly the class of bug
             * the signature-scan white box already taught this file. */
            var ar = st.bgImageAR > 0 ? st.bgImageAR : page.w / page.h;
            var bw = page.w, bh = page.w / ar;
            if (bh < page.h) { bh = page.h; bw = page.h * ar; }
            prims.push({
                type: 'image', src: st.bgImage,
                x: (page.w - bw) / 2, y: (page.h - bh) / 2, w: bw, h: bh,
                opacity: st.bgImageOpacity == null ? 1 : st.bgImageOpacity,
                background: true
            });
            bgInset = st.bgInset == null ? 15 : st.bgInset;
            bgKind = 'image';
            bgIndex = prims.length;
        } else if (st.bg && st.bg !== 'none' && deps.backgrounds) {
            /* Only the CLAIM is read here. An illustration cannot be drawn until
             * it knows where the type will be, and the type cannot be placed
             * until the claim is known — so the claim is a static property and
             * the drawing waits. */
            bgInset = deps.backgrounds.insetOf(st.bg);
            bgKind = st.bg;
            bgIndex = prims.length;
        }

        /* Background guilloché, under everything. */
        if (st.watermark && st.watermark.on && deps.frames) {
            prims = prims.concat(deps.frames.watermark({
                w: page.w, h: page.h,
                cx: st.watermark.cx == null ? null : st.watermark.cx * page.w,
                cy: st.watermark.cy == null ? null : st.watermark.cy * page.h,
                size: st.watermark.size == null ? 0.30 : st.watermark.size,
                opacity: st.watermark.opacity == null ? 0.16 : st.watermark.opacity,
                color: colorOf(st.watermark.color || 'accent', style)
            }));
        }

        /* Frame, and the inset it claims. */
        var inset = st.frameInset != null ? st.frameInset : 16;
        if (deps.frames) {
            var f = deps.frames.build(st.frame || 'classic', {
                w: page.w, h: page.h, ink: style.ink, accent: style.accent
            });
            prims = prims.concat(f.prims);
            if (st.frameInset == null) inset = f.inset;
        }

        /* Content column. `pad` is extra breathing room the author can tighten
         * when overprinting a bought blank, where the frame is already on the
         * paper and our inset is meaningless. */
        var pad = st.pad == null ? 6 : st.pad;
        /* Text has to clear BOTH decorations, so the wider claim wins. Taking
         * the frame's alone is how a name ends up printed over a spray of
         * leaves — the ornament is there, it just is not the one that asked. */
        if (bgInset != null) inset = Math.max(inset, bgInset);
        var content = {
            x: inset + pad,
            y: inset + pad,
            w: page.w - 2 * (inset + pad),
            h: page.h - 2 * (inset + pad)
        };
        if (st.columnW) {          // narrower text column, centred
            var cw = Math.min(st.columnW, content.w);
            content.x += (content.w - cw) / 2;
            content.w = cw;
        }

        /* ── the illustration, now that the column is known ─────────────────
         * The keep-out rectangle is the whole point. Rendering the first nine
         * backgrounds showed the defect an inset alone can never prevent: the
         * type on a certificate runs from the organisation at the top to the
         * signature at the bottom, so ANY art placed in a horizontal band across
         * the sheet lands on it — a stave through the signature line, confetti
         * through the school's name. The margin ring is the only region that is
         * reliably free, so the art is told where it is and draws around it.
         *
         * Spliced at the paper's own z-index rather than appended: it is a
         * background, and appending it would put the leaves over the name. */
        if (bgKind && bgKind !== 'image' && deps.backgrounds) {
            var keepPad = Math.min(6, pad);
            var art = deps.backgrounds.build(bgKind, {
                w: page.w, h: page.h, ink: style.ink, accent: style.accent,
                frames: deps.frames,
                keep: {
                    x0: content.x - keepPad, y0: content.y - keepPad,
                    x1: content.x + content.w + keepPad, y1: content.y + content.h + keepPad
                }
            });
            /* One dial over the whole drawing. Each piece carries the opacity
             * its design wants — a wash is 5 %, a ribbon 30 % — so the control
             * SCALES those rather than replacing them, and the illustration
             * keeps its internal balance at every setting. Above 1 it is
             * allowed to go bolder than drawn: the same art on cream paper and
             * on white wants different weight, and only the author can see
             * which. Capped at 1, not at the multiplier, because opacity is. */
            var lift = st.bgStrength == null ? 1 : Math.max(0.15, Math.min(2, st.bgStrength));
            art.prims.forEach(function (p) {
                p.background = true;
                if (lift !== 1) p.opacity = Math.min(1, (p.opacity == null ? 1 : p.opacity) * lift);
            });
            if (bgIndex >= 0) {
                prims.splice.apply(prims, [bgIndex, 0].concat(art.prims));
                /* The scrim splices at this same mark and must land ABOVE the
                   art it exists to soften — below it, it would be a panel
                   hidden behind the picture, which is the one thing it cannot
                   be and still work. */
                bgIndex += art.prims.length;
            }
        }

        /* ── measure every visible block ─────────────────────────────────── */
        function cellLive(c) {
            return c.on !== false && (String(c.text || '').trim() || String(c.role || '').trim() || String(c.name || '').trim());
        }
        var blocks = (doc.blocks || []).filter(function (b) {
            if (b.on === false) return false;
            if (b.kind === 'rule' || b.kind === 'gap') return true;
            if (b.kind === 'row') return (b.cells || []).some(cellLive);
            return !!String(b.text || '').trim();
        }).map(function (b) {
            if (b.kind !== 'row') return b;
            var copy = assign(b, {});
            copy.cells = (b.cells || []).filter(cellLive);
            return copy;
        });
        var measured = blocks.map(function (b) {
            var fs = {
                font: b.font || 'noto',
                size: b.size || 6,
                bold: !!b.bold,
                italic: !!b.italic,
                letterSpacing: b.letterSpacing || 0
            };
            if (b.kind === 'gap') return { b: b, h: b.size || 6, lines: [], fs: fs };
            if (b.kind === 'rule') return { b: b, h: Math.max(0.6, b.size || 0.5), lines: [], fs: fs };
            if (b.kind === 'row') return measureRow(b, content, measure, warnings);

            var text = b.caps ? String(b.text).toUpperCase() : String(b.text);
            var colW = content.w * (b.widthPct == null ? 1 : b.widthPct);
            var r = fit(text, assign(fs, { maxLines: b.maxLines || 1, minRatio: b.minRatio, tightRatio: b.tightRatio }), colW, measure);
            var fsr = assign(fs, { size: r.size, letterSpacing: r.letterSpacing == null ? fs.letterSpacing : r.letterSpacing });
            var m0 = measure(r.lines[0] || 'X', fsr);
            var lh = (b.lineHeight || 1.22) * r.size;
            var h = m0.asc + m0.desc + (r.lines.length - 1) * lh;
            /* Only a shrink worth ACTING on is reported. The default sample text
               already triggers a 10 % reduction on the organisation line, and a
               tool that greets you with a warning about its own defaults trains
               people to ignore the box that matters. Threshold: 15 %, and never
               for the deliberate one-line pass. */
            if (r.overflow) warnings.push({ block: b.id, code: 'overflow', text: text });
            else if (r.shrunk && !r.tight && r.size < fs.size * 0.85) {
                warnings.push({ block: b.id, code: 'shrunk', text: text, from: fs.size, to: r.size });
            }
            return { b: b, h: h, lines: r.lines, fs: fsr, lh: lh, asc: m0.asc, colW: colW };
        });

        /* ── vertical placement ──────────────────────────────────────────────
         * A certificate is composed in TWO groups, not one column. The body —
         * title, name, reason — is centred in the upper field; the foot — place,
         * date, signature, registration number — is pinned to the bottom of the
         * content box, which is where a document actually carries it.
         *
         * Flowing all of it as one centred stack (the first version) left every
         * A4 landscape template with a third of the page empty below the
         * signature and the signature crowded against the reason. Nothing about
         * that reads as a document; it reads as a centred div.
         *
         * A block carrying `pin: 'bottom'` opens the foot group, and everything
         * after it belongs to that group. */
        var gaps = measured.map(function (m, i) { return i === 0 ? 0 : (m.b.gapBefore == null ? 3 : m.b.gapBefore); });
        var pinAt = -1;
        for (var pi = 0; pi < measured.length; pi++) {
            if (measured[pi].b.pin === 'bottom') { pinAt = pi; break; }
        }
        var vAlign = st.vAlign || 'center';
        var placed = [];

        function heightOf(from, to) {
            var t = 0;
            for (var i = from; i < to; i++) t += measured[i].h + (i === from ? 0 : gaps[i]);
            return t;
        }

        var bodyEnd = pinAt < 0 ? measured.length : pinAt;
        var bodyH = heightOf(0, bodyEnd);
        var footH = pinAt < 0 ? 0 : heightOf(pinAt, measured.length);
        /* The foot claims its own height plus the gap the author asked for
         * between the two groups; the body is centred in what is left. */
        var footGap = pinAt < 0 ? 0 : Math.max(0, gaps[pinAt]);
        var field = content.h - (pinAt < 0 ? 0 : footH + footGap);
        var extra = field - bodyH;
        var spread = 0;
        var y = content.y;
        if (vAlign === 'center') y += Math.max(0, extra) / 2;
        else if (vAlign === 'bottom') y += Math.max(0, extra);
        else if (vAlign === 'spread' && bodyEnd > 1 && extra > 0) spread = extra / (bodyEnd - 1);
        if (extra < -0.5) warnings.push({ code: 'page_overflow', over: Math.round(-extra * 10) / 10 });

        measured.forEach(function (m, i) {
            if (i === pinAt) {
                /* Hand the pen to the bottom of the box. Never above where the
                 * body actually ended — on a crowded page the foot yields
                 * rather than printing over the reason. */
                y = Math.max(y + footGap, content.y + content.h - footH);
                spread = 0;
                y -= gaps[i];
            }
            y += gaps[i] + (i && i !== pinAt ? spread : 0);
            var b = m.b;
            var dx = b.dx || 0, dy = b.dy || 0;
            var align = b.align || 'center';
            var colX = content.x + (content.w - (m.colW == null ? content.w : m.colW)) / 2;
            var ax = align === 'left' ? colX
                : align === 'right' ? colX + (m.colW == null ? content.w : m.colW)
                : colX + (m.colW == null ? content.w : m.colW) / 2;

            if (b.kind === 'gap') { y += m.h; return; }

            if (b.kind === 'row') {
                placeRow(m, content, y + dy, style, prims, placed, dx);
                y += m.h;
                return;
            }

            if (b.kind === 'rule') {
                var rw = (m.colW == null ? content.w : m.colW) * (b.rulePct == null ? 0.42 : b.rulePct);
                var rx = align === 'left' ? colX : align === 'right' ? colX + (m.colW || content.w) - rw : colX + ((m.colW || content.w) - rw) / 2;
                prims.push({
                    type: 'path', d: 'M' + r2(rx + dx) + ' ' + r2(y + dy) + 'L' + r2(rx + rw + dx) + ' ' + r2(y + dy),
                    color: colorOf(b.color, style), w: Math.max(0.15, b.size || 0.35), fill: null, opacity: b.opacity == null ? 1 : b.opacity
                });
                y += m.h;
                placed.push({ id: b.id, kind: 'rule', x: rx + dx, y: y + dy, w: rw, h: m.h });
                return;
            }

            var top = y;
            m.lines.forEach(function (line, li) {
                var baseline = top + m.asc + li * m.lh;
                prims.push({
                    type: 'text', s: line, w: measure(line, m.fs).w, blockId: b.id, line: li,
                    x: ax + dx, y: baseline + dy,
                    size: m.fs.size, font: m.fs.font, bold: m.fs.bold, italic: m.fs.italic,
                    letterSpacing: m.fs.letterSpacing,
                    anchor: svgAnchor(align),
                    color: colorOf(b.color, style),
                    opacity: b.opacity == null ? 1 : b.opacity
                });
            });
            var wMax = 0;
            m.lines.forEach(function (l) { wMax = Math.max(wMax, measure(l, m.fs).w); });
            placed.push({
                id: b.id, kind: b.kind, x: ax + dx, y: top + dy, w: wMax, h: m.h,
                anchor: svgAnchor(align), size: m.fs.size, lines: m.lines.length,
                letterSpacing: m.fs.letterSpacing || 0
            });
            y += m.h;
        });

        /* ── overlays: logo, signature image, stamp, medallion, QR ───────── */
        (doc.overlays || []).forEach(function (ov) {
            if (ov.on === false) return;
            var w = ov.w || 24;
            var h = ov.h || w;
            var cx = (ov.x == null ? 0.5 : ov.x) * page.w;
            var cy = (ov.y == null ? 0.5 : ov.y) * page.h;
            if (ov.kind === 'medallion' && deps.frames) {
                prims = prims.concat(deps.frames.medallion(cx, cy, w / 2,
                    colorOf(ov.color || '#a52a2a', style), colorOf(ov.accent || 'accent', style)));
                placed.push({ id: ov.id, kind: 'overlay', x: cx - w / 2, y: cy - w / 2, w: w, h: w * 2.4, overlay: true });
                return;
            }
            if (ov.kind === 'image' && ov.src) {
                prims.push({
                    type: 'image', src: ov.src, x: cx - w / 2, y: cy - h / 2, w: w, h: h,
                    opacity: ov.opacity == null ? 1 : ov.opacity, rot: ov.rot || 0
                    /* No blend flag: a scan's white is removed at upload (see
                       keyOutWhite in index.js), because pdf-lib cannot blend and
                       a preview-only blend is a lie about the printed sheet. */
                });
                placed.push({ id: ov.id, kind: 'overlay', x: cx - w / 2, y: cy - h / 2, w: w, h: h, overlay: true });
                return;
            }
            if (ov.kind === 'qr' && ov.matrix) {
                var n = ov.matrix.length, cell = w / n, d = '';
                for (var r = 0; r < n; r++) {
                    for (var c = 0; c < n; c++) {
                        if (!ov.matrix[r][c]) continue;
                        var X = cx - w / 2 + c * cell, Y = cy - w / 2 + r * cell;
                        d += 'M' + r2(X) + ' ' + r2(Y) + 'h' + r2(cell) + 'v' + r2(cell) + 'h' + r2(-cell) + 'Z';
                    }
                }
                prims.push({ type: 'path', d: d, fill: colorOf(ov.color || 'ink', style), color: null, w: 0 });
                placed.push({ id: ov.id, kind: 'overlay', x: cx - w / 2, y: cy - w / 2, w: w, h: w, overlay: true });
                return;
            }
        });

        /* ── the scrim ──────────────────────────────────────────────────────
         * A soft panel of paper laid between the illustration and the type.
         * It is the difference between "a picture with words on it" and a
         * document: without one, a name in dark ink crossing a dark branch is
         * unreadable at exactly the size it matters most.
         *
         * It is computed AFTER the type, from where the type actually landed —
         * a panel sized to the whole content column is a slab that hides the
         * art the author chose. And it is INSERTED at the background's z-index
         * rather than appended, so it covers the illustration and nothing else:
         * appended, it would lie over the frame and the signature.
         *
         * Only over a background. On plain paper it would be an invisible
         * rectangle that costs a primitive on every page of a batch. */
        if (bgKind && st.scrim && st.scrim.on !== false && bgIndex >= 0) {
            var pads = st.scrim.pad == null ? 7 : st.scrim.pad;
            /* ⚠️ Measured over the TEXT PRIMITIVES, not over `placed`. The
               signature row draws its role and name straight into `prims`
               without a block entry, so a union of blocks left the one line the
               scrim exists for — the signature — hanging outside the panel. The
               ink is the thing to cover, so the ink is the thing to measure. */
            var box = null;
            prims.forEach(function (t) {
                if (t.type !== 'text' || !String(t.s || '').trim()) return;
                var tw = t.w || t.size * 2;
                var x0 = t.anchor === 'middle' ? t.x - tw / 2 : t.anchor === 'end' ? t.x - tw : t.x;
                var y0 = t.y - t.size * 0.85, y1 = t.y + t.size * 0.35;
                if (!box) box = { x0: x0, y0: y0, x1: x0 + tw, y1: y1 };
                else {
                    box.x0 = Math.min(box.x0, x0); box.y0 = Math.min(box.y0, y0);
                    box.x1 = Math.max(box.x1, x0 + tw); box.y1 = Math.max(box.y1, y1);
                }
            });
            if (box && box.x1 - box.x0 > 8 && box.y1 - box.y0 > 8) {
                /* ⚠️ Clamp to the PAGE, not to a mirror of the top-left inset.
                   `page.h - 2 * sy` quietly assumes the panel is centred, and a
                   certificate's type is not: it starts 47 mm down and ends at
                   184, so the mirror cut the panel off at 163 and left the
                   signature — the line the panel exists for — outside it. */
                var sx = Math.max(2, box.x0 - pads), sy = Math.max(2, box.y0 - pads);
                var sw = Math.min(page.w - 2, box.x1 + pads) - sx;
                var sh = Math.min(page.h - 2, box.y1 + pads) - sy;
                var scrimColor = st.scrim.color === 'ink' ? style.ink
                    : (st.scrim.color && st.scrim.color.charAt(0) === '#') ? st.scrim.color
                    : (style.paper === 'none' ? '#ffffff' : style.paper);
                /* ⚠️ A PATH, not a rect. Rounded corners are the difference
                   between «part of the design» and «a slab pasted over the
                   photograph» — and pdf-lib's drawRectangle has no radius at
                   all, so a rounded RECT would round on screen and print square.
                   A path rounds in all three. x/y/w/h ride along for the
                   preflight's bounding box and for the tests. */
                var rr = Math.max(0, Math.min(st.scrim.radius == null ? 6 : st.scrim.radius,
                                              Math.min(sw, sh) / 2));
                prims.splice(bgIndex, 0, {
                    type: 'path', d: roundedRect(sx, sy, sw, sh, rr),
                    /* ⚠️ NOT `w`/`h`: on a path primitive `w` is the STROKE
                       WIDTH, and a panel 217 mm wide would have asked for a
                       217 mm stroke. It draws nothing only because `color` is
                       null, which is the kind of accident that survives until
                       someone gives the panel a border. */
                    sx: r2(sx), sy: r2(sy), sw: r2(sw), sh: r2(sh),
                    fill: scrimColor, color: null,
                    opacity: st.scrim.alpha == null ? 0.72 : st.scrim.alpha,
                    scrim: true
                });
            }
        }

        return { prims: prims, page: page, content: content, blocks: placed, warnings: warnings };
    }

    /* -- rows ---------------------------------------------------------------
     * The foot of a certificate is not a stack: it is a date on one side and a
     * signature on the other, and forcing it through the vertical flow is why
     * generators end up with the date centred above the signature -- which is
     * not how any real document is set.
     *
     * A `sign` cell draws what a Russian document actually carries: the role,
     * a rule long enough to sign on, and the signatory's name UNDER the rule in
     * smaller type. Competitors make the author place three separate text boxes
     * and align them by eye. */
    function measureRow(b, content, measure, warnings) {
        var cells = (b.cells || []).map(function (c) {
            var fs = { font: c.font || b.font || 'noto', size: c.size || b.size || 4.6, bold: !!c.bold, italic: !!c.italic, letterSpacing: c.letterSpacing || 0 };
            var cw = content.w * (c.wPct == null ? 0.45 : c.wPct);
            if (c.kind === 'sign') {
                var roleM = measure(c.role || 'X', fs);
                var nameFs = assign(fs, { size: fs.size * 0.92 });
                var nameM = measure(c.name || 'X', nameFs);
                return {
                    c: c, fs: fs, nameFs: nameFs, cw: cw, asc: roleM.asc,
                    h: roleM.asc + roleM.desc + (c.name ? nameM.asc + nameM.desc + fs.size * 0.35 : 0)
                };
            }
            var text = c.caps ? String(c.text || '').toUpperCase() : String(c.text || '');
            var r = fit(text, assign(fs, { maxLines: c.maxLines || 2, minRatio: c.minRatio, tightRatio: c.tightRatio }), cw, measure);
            var fsr = assign(fs, { size: r.size, letterSpacing: r.letterSpacing == null ? fs.letterSpacing : r.letterSpacing });
            var m0 = measure(r.lines[0] || 'X', fsr);
            var lh = (c.lineHeight || 1.2) * r.size;
            if (r.overflow) warnings.push({ block: b.id, code: 'overflow', text: text });
            return {
                c: c, fs: fsr, cw: cw, lines: r.lines, lh: lh, asc: m0.asc,
                h: m0.asc + m0.desc + (r.lines.length - 1) * lh
            };
        });
        var h = cells.reduce(function (mx, c) { return Math.max(mx, c.h); }, 0);
        return { b: b, h: h, cells: cells, lines: [], fs: { size: b.size || 4.6 } };
    }

    function placeRow(m, content, top, style, prims, placed, dx) {
        m.cells.forEach(function (cell) {
            var c = cell.c;
            var x0 = content.x + content.w * (c.xPct == null ? 0 : c.xPct) + dx;
            var cw = cell.cw;
            var align = c.align || 'left';
            var ax = align === 'left' ? x0 : align === 'right' ? x0 + cw : x0 + cw / 2;

            if (c.kind === 'sign') {
                var base = top + cell.asc;
                var roleW = c.role ? _measure(c.role, cell.fs).w : 0;
                var gap = c.role ? cell.fs.size * 0.5 : 0;
                var lineX0 = x0 + roleW + gap;
                var lineX1 = x0 + cw;
                if (c.role) {
                    prims.push({
                        type: 'text', s: c.role, w: roleW, blockId: c.id, role: 'role',
                        x: x0, y: base, size: cell.fs.size, font: cell.fs.font,
                        bold: !!c.bold, italic: false, letterSpacing: 0, anchor: 'start',
                        color: colorOf(c.color, style), opacity: 1
                    });
                }
                if (lineX1 - lineX0 > 4) {
                    prims.push({
                        type: 'path',
                        d: 'M' + r2(lineX0) + ' ' + r2(base + cell.fs.size * 0.28) + 'L' + r2(lineX1) + ' ' + r2(base + cell.fs.size * 0.28),
                        color: colorOf(c.lineColor || 'muted', style), w: 0.25, fill: null, opacity: 1
                    });
                }
                if (c.name) {
                    prims.push({
                        type: 'text', s: c.name, w: _measure(c.name, cell.nameFs).w, blockId: c.id, role: 'signName',
                        x: (lineX0 + lineX1) / 2, y: base + cell.fs.size * 1.32,
                        size: cell.nameFs.size, font: cell.nameFs.font, bold: false, italic: false, letterSpacing: 0,
                        anchor: 'middle', color: colorOf(c.nameColor || 'muted', style), opacity: 1
                    });
                }
                placed.push({ id: c.id, kind: 'signcell', x: x0, y: top, w: cw, h: cell.h });
                return;
            }

            (cell.lines || []).forEach(function (line, li) {
                prims.push({
                    type: 'text', s: line, w: _measure(line, cell.fs).w, blockId: c.id, line: li,
                    x: ax, y: top + cell.asc + li * cell.lh,
                    size: cell.fs.size, font: cell.fs.font, bold: cell.fs.bold, italic: cell.fs.italic,
                    letterSpacing: cell.fs.letterSpacing,
                    anchor: svgAnchor(align),
                    color: colorOf(c.color, style), opacity: c.opacity == null ? 1 : c.opacity
                });
            });
            placed.push({ id: c.id, kind: 'cell', x: x0, y: top, w: cw, h: cell.h });
        });
    }

    /* placeRow needs the measurer; layout keeps exactly one reference to it so
       the injection stays a single seam rather than a parameter threaded
       through four call sites. */
    var _measure = null;

    function r2(v) { return Math.round(v * 100) / 100; }

    return {
        build: build, pageSize: pageSize, PAGES: PAGES,
        fit: fit, wrap: wrapAll, colorOf: colorOf, mix: mix
    };
});
